import { Type, destroy, read, setCookies, write } from '@acorns/web-utils'
import { ApolloClient } from 'apollo-client'

import { USER_UUID } from 'app-constants'
import { AuthSession } from 'generated/types'

import logoutMutation from '../graphql/mutations/session/logout.gql'
import refreshAuthTokensMutation from '../graphql/mutations/session/refreshAuthTokens.gql'
import { debounce } from 'lodash'

export const SESSION_REFRESH_QUEUED_SESSION_STORAGE_KEY = 'session_refresh_queued'

const refreshCookie = (type: Type) => {
  const cookieValue = read(type)

  if (cookieValue) {
    // Rewrites cookie for the purpose of extending max-age.
    write(type, cookieValue)
  }
}

const isImplicitCookieAuth = () => {
  return !!read(Type.csrfToken)
}

const refreshJwtAuthCookies = async (apolloClient: ApolloClient<object>) => {
  const refreshToken = isImplicitCookieAuth() ? '' : read(Type.refreshToken)
  const response = await apolloClient.mutate({
    mutation: refreshAuthTokensMutation,
    variables: {
      // IMPORTANT:
      // For "implicit cookie auth", the GraphQL gateway will replace the value for the `refreshToken` variable
      // with the token value retrieved from the HttpOnly cookie storing the refresh token.
      // However, a "dummy" variable with the name `refreshToken` NEEDS to be defined for this to work properly.
      refreshToken,
    },
  })

  if (!isImplicitCookieAuth() && response.data) {
    // This is for local development only, which doesn't use implicit cookie auth.
    const { refreshAuthToken } = response.data
    write(Type.token, refreshAuthToken.token)
    write(Type.refreshToken, refreshAuthToken.refreshToken)
  }
}

export const refreshSessionCookies = async (apolloClient: ApolloClient<object>) => {
  await refreshJwtAuthCookies(apolloClient)

  // Important: The expiration for the partition cookie needs to extended too.
  // acorns-hello doesn't (currently) use this cookie, but it is required for web-app's release rollout mechanism (web-roller-lambda).
  // If this cookie does not exist, web-roller-lambda treats the customer as unauthenticated for authenticated web-app routes and redirects to login-app.
  refreshCookie(Type.partition)

  window.sessionStorage.removeItem(SESSION_REFRESH_QUEUED_SESSION_STORAGE_KEY)
}

export const debouncedRefreshSessionCookies = debounce(
  refreshSessionCookies,
  5000, // 5 seconds
)

export const closeSession = async (apolloClient: ApolloClient<object>) => {
  // Avoid calling the `logout` mutation unnecessarily; call it only when we know we have auth tokens to expire.
  const hasAuthTokens = read(Type.csrfToken)
  if (hasAuthTokens) {
    try {
      await apolloClient.mutate({
        mutation: logoutMutation,
      })
    } catch {
      // Intentionally do nothing;
      // Even an error response is expected to contain the necessary HTTP headers to delete/expire the auth-related cookies.
    } finally {
      // If for whatever reason the server response failed to delete the auth tokens, then at least destroy the cookie storing the CSRF token.
      // This will render the access token "useless" for implicit cookie auth.
      destroy(Type.csrfToken)
    }
  }

  destroy(Type.token)
  destroy(Type.partition)
  window.sessionStorage.removeItem(USER_UUID)

  // destroying the refresh token cookie is only relevant when developing locally
  destroy(Type.refreshToken)
}

export const isAuthenticated = () => {
  return Boolean(read(Type.token)) || Boolean(read(Type.csrfToken))
}

export const setAuthItems = (
  authSession: Pick<AuthSession, 'token' | 'refreshToken' | 'identityId'>,
) => {
  setCookies(authSession)

  window.sessionStorage.setItem(USER_UUID, authSession.identityId)
}
